import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  Signal,
  WritableSignal,
} from '@angular/core';
import { DsTooltipDirectiveModule, SelectListOption, SuggestionItem } from '@bmw-ds/components';

import { SuggestedAddress } from '../../../clients/tolerant-post/model/view.model';
import { AddressField } from '../../../customer/model/address-field.model';
import { CustomerAddress } from '../../../customer/model/customer-information.model';
import { GCDM_MAX_LENGTH } from '../../../customer/model/gcdm-constraints';
import { AddressViewService, SuggestionOfAddressField } from '../../../customer/services/address.view-service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { lensProxy } from '../../../utils/lensProxy/lensProxy';
import { InputFieldComponent } from '../input-field/input-field.component';
import { SelectFieldComponent } from '../select-field/select-field.component';

interface Flex {
  basis?: string;
  grow?: number;
}

@Component({
  selector: 'cdf-regional-address-field',
  host: {
    class: 'flex-shrink-0',
    '[style.flex-basis]': 'flex().basis',
    '[style.flex-grow]': 'flex().grow',
  },
  imports: [SelectFieldComponent, InputFieldComponent, DsTooltipDirectiveModule],
  template: `
    @if (options(); as options) {
      <cdf-select-field
        [(model)]="addressFieldModel"
        [id]="addressField()"
        [placeholder]="i18n.enum(addressField(), 'placeholder.addressField')"
        [options]="options"
        [disabled]="isSelectFieldDisabled()"
        [ds-tooltip]="disabledTooltip()"
        [dsTooltipConfig]="{ disabled: !disabled() || !disabledTooltip() }"
        [isClearable]="isClearable()"
        [displayError]="displayError()"
        [errorMessage]="errorMessage()"
      />
    } @else {
      <cdf-input-field
        [(model)]="addressFieldModel"
        [id]="addressField()"
        [maxLength]="maxLength()"
        [placeholder]="i18n.enum(addressField(), 'placeholder.addressField')"
        [disabled]="disabled() || (disableIfNoCountry() && !address()?.country)"
        [ds-tooltip]="disabledTooltip()"
        [dsTooltipConfig]="{ disabled: !disabled() || !disabledTooltip() }"
        [suggestions]="suggestionItems()"
        [displayError]="displayError()"
        [errorMessage]="errorMessage()"
        (suggestionSelected)="
          selectedSuggestionForAddressField.emit({ addressField: addressField(), suggestion: $event })
        "
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegionalAddressFieldComponent {
  readonly i18n = useI18n();
  readonly addressViewService = inject(AddressViewService);

  readonly addressField = input.required<AddressField>();
  readonly address = model.required<CustomerAddress | undefined>();

  readonly availableCountries = input<SelectListOption[]>();
  readonly availableProvinces = input<SelectListOption[]>([]);
  readonly disabled = input(false);

  readonly isClearable = input(false, { transform: booleanAttribute });

  readonly displayError = input(false);
  readonly errorMessage = input<string>();

  readonly suggestions = input<SuggestedAddress[] | undefined>(undefined);

  // Disable all other input fields, if not country is set.
  // This might be required if the country parameter is required for auto-completion
  readonly disableIfNoCountry = input(false, { transform: booleanAttribute });
  readonly disabledTooltip = input<string>('');

  readonly selectedSuggestionForAddressField = output<SuggestionOfAddressField>();

  protected readonly addressProxy = lensProxy(this.address);

  /** The options to offer for this dropdown. Undefined for text fields */
  readonly options = computed<SelectListOption[] | undefined>(() => {
    if (this.addressField() === AddressField.COUNTRY) {
      return this.availableCountries() ?? [];
    } else if (this.addressField() === AddressField.PROVINCE) {
      return this.availableProvinces() ?? []; // friendly-name options for the current country
    } else {
      return undefined;
    }
  });

  readonly isSelectFieldDisabled = computed(
    () =>
      this.disabled() ||
      (this.disableIfNoCountry() && !this.address()?.country && this.addressField() !== AddressField.COUNTRY),
  );

  protected static readonly cellWidths: Partial<Record<AddressField, string>> = {
    [AddressField.HOUSE_NUMBER]: '5ch',
    [AddressField.POSTAL_CODE]: '10ch',
  };

  protected readonly flex = computed((): Flex => {
    const cellWidth = RegionalAddressFieldComponent.cellWidths[this.addressField()];
    // Fields with fixed widths still have a non-zero grow, so they occupy the whole row when they wrap to a new row.
    // However, other fields have a much higher grow, to prevent the fixed width columns from growing on the same line.
    return cellWidth ? { basis: cellWidth, grow: 1 } : { basis: '12ch', grow: 10000 };
  });

  /**
   * GCDM's maxLength for this field. The dropdown fields (country, province) are not limited:
   * their value comes from a fixed option list.
   */
  protected static readonly maxLengths: Partial<Record<AddressField, number>> = {
    [AddressField.STREET]: GCDM_MAX_LENGTH.street,
    [AddressField.HOUSE_NUMBER]: GCDM_MAX_LENGTH.houseNumber,
    [AddressField.POSTAL_CODE]: GCDM_MAX_LENGTH.postalCode,
    [AddressField.CITY]: GCDM_MAX_LENGTH.city,
    [AddressField.ADDRESS_LINE_1]: GCDM_MAX_LENGTH.addressLine1,
    [AddressField.ADDRESS_LINE_2]: GCDM_MAX_LENGTH.addressLine2,
    [AddressField.POST_BOX]: GCDM_MAX_LENGTH.postBox,
  };

  protected readonly maxLength = computed(() => RegionalAddressFieldComponent.maxLengths[this.addressField()]);

  readonly suggestionItems: Signal<SuggestionItem[]> = computed(() => {
    const suggestions = this.suggestions();
    if (!suggestions) return [];

    const suggestedValues = suggestions
      .map((suggestion) => this.addressViewService.getSuggestedAddressUiFieldValue(this.addressField(), suggestion))
      .filter((value): value is string => !!value);

    return [
      // remove duplicates
      ...new Set(suggestedValues),
    ].map((label): SuggestionItem => ({ label }));
  });

  get addressFieldModel(): WritableSignal<string | undefined> {
    switch (this.addressField()) {
      case AddressField.PROVINCE:
        return this.addressProxy.province;
      case AddressField.COUNTRY:
        return this.addressProxy.country;
      case AddressField.HOUSE_NUMBER:
        return this.addressProxy.houseNumber;
      case AddressField.POSTAL_CODE:
        return this.addressProxy.postalCode;
      case AddressField.STREET:
        return this.addressProxy.street;
      case AddressField.CITY:
        return this.addressProxy.city;
      case AddressField.ADDRESS_LINE_1:
        return this.addressProxy.addressLine1;
      case AddressField.ADDRESS_LINE_2:
        return this.addressProxy.addressLine2;
      case AddressField.POST_BOX:
        return this.addressProxy.postBox;
      default:
        throw new RangeError(
          'Unknown address field: in RegionalAddressFieldComponent.addressFieldModel ' + this.addressField(),
        );
    }
  }
}
